import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, ROLES } from '../../context/AppContext';
import { toast } from 'sonner';
import {
  MapPin,
  Search,
  Image as ImageIcon,
  Compass,
  Download,
  Trash2,
  Check,
  RefreshCw,
  Plus,
  Edit,
  ZoomIn,
  ZoomOut,
  Globe,
  Map,
  Navigation,
  Info
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { fetchCases, saveGeotag } from '../../api';

export const ManualGeoTagger = () => {
  const navigate = useNavigate();
  const { currentRole } = useApp();

  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let isMounted = true;
    fetchCases().then(res => {
      if (!isMounted) return;
      const list = Array.isArray(res) ? res : [];
      const mapped = list.map(c => ({
        ...c,
        images: (Array.isArray(c.verifiedImages) && c.verifiedImages.length > 0)
          ? c.verifiedImages.map((img, i) => ({
              id: img.id || `IMG${i + 1}`,
              label: img.label || `Image ${i + 1}`,
              url: img.url || '',
              lat: img.latitude || null,
              lng: img.longitude || null,
              address: img.address || '',
              city: c.city || '',
              state: c.state || '',
              pincode: c.pincode || '',
              isTagged: !!(img.latitude && img.longitude)
            }))
          : []
      }));
      setCases(mapped);
      if (mapped.length > 0) {
        setSelectedCaseId(mapped[0].id);
      }
    }).catch(err => {
      console.warn('Failed to load cases in GeoTagger:', err);
    });
    return () => { isMounted = false; };
  }, []);

  const currentCase = cases.find(c => c.id === selectedCaseId);
  const [selectedImageId, setSelectedImageId] = useState(currentCase?.images?.[0]?.id || '');
  const currentImage = currentCase?.images?.find(img => img.id === selectedImageId);

  // Form states
  const [latInput, setLatInput] = useState('23.0225');
  const [lngInput, setLngInput] = useState('72.5714');
  const [addressInput, setAddressInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [stateInput, setStateInput] = useState('');
  const [pincodeInput, setPincodeInput] = useState('');
  const [isTaggingActive, setIsTaggingActive] = useState(false);
  const [pinPosition, setPinPosition] = useState({ x: 150, y: 150 });
  const [zoomLevel, setZoomLevel] = useState(14);
  const [mapType, setMapType] = useState('roadmap'); // roadmap or terrain

  // Sync inputs on image/case change
  useEffect(() => {
    if (currentImage) {
      const lat = currentImage.lat;
      const lng = currentImage.lng;
      setLatInput(lat !== null && lat !== undefined ? lat.toFixed(6) : '');
      setLngInput(lng !== null && lng !== undefined ? lng.toFixed(6) : '');
      setAddressInput(currentImage.address || '');
      setCityInput(currentImage.city || '');
      setStateInput(currentImage.state || '');
      setPincodeInput(currentImage.pincode || '');
      setIsTaggingActive(!!currentImage.isTagged);

      // Calculate a mock pin placement based on coordinates
      if (lat && lng) {
        const xVal = 150 + (lat - 23.0225) * 5000;
        const yVal = 150 - (lng - 72.5714) * 5000;
        setPinPosition({
          x: Math.max(20, Math.min(280, xVal)),
          y: Math.max(20, Math.min(280, yVal))
        });
      } else {
        // Untagged defaults
        setPinPosition({ x: 150, y: 150 });
      }
    }
  }, [selectedImageId, selectedCaseId, currentImage]);

  if (currentRole === ROLES.CLIENT) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] p-6 text-center">
        <div className="w-20 h-20 bg-rose-50 border border-rose-100 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-rose-100/50 animate-bounce">
          <Info className="w-10 h-10 text-rose-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Access Restricted</h2>
        <p className="text-slate-500 mt-2 max-w-md text-sm leading-relaxed">
          Manual Geo Tagging is restricted for Client accounts. You do not have permission to view or manipulate coordinate parameters.
        </p>
        <Button
          onClick={() => navigate('/')}
          className="mt-6 bg-slate-800 hover:bg-slate-900 text-white font-semibold shadow-md px-6 py-2.5 rounded-lg text-xs"
        >
          Go Back to Dashboard
        </Button>
      </div>
    );
  }

  // Triggered when selecting another case
  const handleCaseChange = (caseId) => {
    setSelectedCaseId(caseId);
    const newCase = cases.find(c => c.id === caseId);
    if (newCase && newCase.images.length) {
      setSelectedImageId(newCase.images[0].id);
    } else {
      setSelectedImageId('');
    }
  };

  // Triggered when selecting another image inside selected case
  const handleImageChange = (img) => {
    setSelectedImageId(img.id);
  };

  // Start tagging session for untagged image
  const handleAddGeoTagClick = () => {
    setIsTaggingActive(true);
    // Set decent starting coordinates (Ahmedabad center)
    const defaultLat = 23.0225;
    const defaultLng = 72.5714;
    setLatInput(defaultLat.toFixed(6));
    setLngInput(defaultLng.toFixed(6));
    setAddressInput('Commerce Six Road, Navrangpura');
    setCityInput('Ahmedabad');
    setStateInput('Gujarat');
    setPincodeInput('380009');
    setPinPosition({ x: 150, y: 150 });

    toast.success('Geotagging initialized! Drag map pin or punch coordinates/address details.');
  };

  // Simulated coordinate address lookup (Reverse geocode)
  const handleAddressFetch = () => {
    const latVal = parseFloat(latInput);
    const lngVal = parseFloat(lngInput);

    if (isNaN(latVal) || isNaN(lngVal)) {
      toast.error('Please enter valid coordinates before fetching address');
      return;
    }

    const mockAddresses = [
      { address: 'Ganesh Meridian, Block C, Sola Road', city: 'Ahmedabad', state: 'Gujarat', pincode: '380061' },
      { address: 'Commerce Six Road, Navrangpura Area', city: 'Ahmedabad', state: 'Gujarat', pincode: '380009' },
      { address: 'Parimal Garden Cross Road, Ambawadi', city: 'Ahmedabad', state: 'Gujarat', pincode: '380006' },
      { address: 'Law Garden Food Court, Ellisbridge', city: 'Ahmedabad', state: 'Gujarat', pincode: '380006' },
      { address: 'Vastrapur Lake Road, Opp Lake', city: 'Ahmedabad', state: 'Gujarat', pincode: '380015' }
    ];

    const picked = mockAddresses[Math.floor(Math.random() * mockAddresses.length)];
    setAddressInput(picked.address);
    setCityInput(picked.city);
    setStateInput(picked.state);
    setPincodeInput(picked.pincode);

    toast.success('Reverse address lookup resolved successfully!');
  };

  // Save changes back to image state
  const handleSaveGeoTag = () => {
    const latVal = parseFloat(latInput);
    const lngVal = parseFloat(lngInput);

    if (isNaN(latVal) || isNaN(lngVal)) {
      toast.error('Please provide numeric latitude and longitude values');
      return;
    }

    if (!addressInput.trim() || !cityInput.trim() || !stateInput.trim() || !pincodeInput.trim()) {
      toast.error('Please fill in all address fields (Address, City, State, Pincode)');
      return;
    }

    setCases(prevCases => prevCases.map(c => {
      if (c.id === selectedCaseId) {
        return {
          ...c,
          images: c.images.map(img => {
            if (img.id === selectedImageId) {
              return {
                ...img,
                lat: latVal,
                lng: lngVal,
                address: addressInput.trim(),
                city: cityInput.trim(),
                state: stateInput.trim(),
                pincode: pincodeInput.trim(),
                isTagged: true
              };
            }
            return img;
          })
        };
      }
      return c;
    }));

    toast.success('EXIF GPS and Address metadata written and saved successfully!');
  };

  // Image action simulations
  const handleDownloadImage = (img) => {
    toast.success(`Downloading ${img.label} with embedded EXIF (${img.lat || 'N/A'}, ${img.lng || 'N/A'})`);
  };

  const handleDeleteImage = (imgId) => {
    setCases(prevCases => prevCases.map(c => {
      if (c.id === selectedCaseId) {
        return {
          ...c,
          images: c.images.filter(img => img.id !== imgId)
        };
      }
      return c;
    }));
    if (selectedImageId === imgId) {
      setSelectedImageId('');
    }
    toast.success('Image deleted from verification bundle');
  };

  // Simulated drag-drop movement on mockup map
  const handleMapClickOrDrag = (e) => {
    if (!isTaggingActive) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // limit pin to box boundaries
    const boundedX = Math.max(15, Math.min(rect.width - 15, x));
    const boundedY = Math.max(15, Math.min(rect.height - 15, y));

    setPinPosition({ x: boundedX, y: boundedY });

    // Generate new mock lat/lng based on pixel position
    const baseLat = 23.0225;
    const baseLng = 72.5714;
    const calculatedLat = baseLat + (boundedY - 150) * -0.0001;
    const calculatedLng = baseLng + (boundedX - 150) * 0.0001;

    setLatInput(calculatedLat.toFixed(6));
    setLngInput(calculatedLng.toFixed(6));
    setAddressInput('Custom Map Pin Placement');
  };

  // Search case filter
  const filteredCases = cases.filter(c =>
    c.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.refNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 text-slate-800 min-h-screen bg-slate-50">
      {/* Title / Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <MapPin className="w-8 h-8 text-blue-600 animate-pulse" />
            Manual Geo Tagger
          </h1>
          <p className="text-slate-500 mt-1 text-sm font-medium">
            Prism Spatial Overrides — Drag pin & manually override EXIF coordinate details & address verification records.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setMapType(prev => prev === 'roadmap' ? 'terrain' : 'roadmap')}
            className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm"
          >
            <Globe className={`w-4 h-4 mr-2 ${mapType === 'terrain' ? 'text-emerald-500' : 'text-blue-500'}`} />
            {mapType === 'roadmap' ? 'Terrain View' : 'Roadmap View'}
          </Button>
          {isTaggingActive && (
            <Button onClick={handleSaveGeoTag} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md shadow-blue-200 transition-all">
              <Check className="w-4 h-4 mr-2" />
              Save Coordinates
            </Button>
          )}
        </div>
      </div>

      {/* Main Split Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left Side: Cases & Photos (5 Cols) */}
        <div className="lg:col-span-5 space-y-6">

          {/* Case Selector Card */}
          <Card className="bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow p-5 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-indigo-500" />
                Select Verification Case
              </h3>
              <span className="text-xs text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-0.5 rounded-full font-bold">
                {filteredCases.length} Cases
              </span>
            </div>

            {/* Search Bar */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search Applicant, Ref Number, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              />
            </div>

            {/* Cases Scroll List */}
            <div className="max-h-60 overflow-y-auto space-y-2 pr-1 scrollbar-thin">
              {filteredCases.length === 0 ? (
                <div className="py-8 text-center text-slate-400">
                  <p className="text-sm font-medium">No matching cases found</p>
                </div>
              ) : (
                filteredCases.map(c => {
                  const isActive = c.id === selectedCaseId;
                  const taggedCount = c.images.filter(img => img.isTagged).length;
                  return (
                    <div
                      key={c.id}
                      onClick={() => handleCaseChange(c.id)}
                      className={`p-3.5 rounded-xl border transition-all cursor-pointer flex justify-between items-center ${isActive
                        ? 'bg-blue-50/60 border-blue-200 text-slate-900 font-medium shadow-sm'
                        : 'bg-white border-slate-100 hover:bg-slate-50 hover:border-slate-200 text-slate-700'
                        }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-bold truncate ${isActive ? 'text-blue-900' : 'text-slate-800'}`}>
                          {c.applicantName}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                          <span className="font-mono bg-slate-100 text-slate-600 px-1 rounded">{c.refNumber}</span>
                          <span>•</span>
                          <span>{c.product?.name || c.product || 'Verification'}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${c.status === 'completed'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                          : 'bg-amber-50 text-amber-700 border border-amber-100'
                          }`}>
                          {c.status}
                        </span>
                        <p className="text-[10px] text-slate-500 mt-1.5 font-medium">
                          Tagged: <span className="font-semibold text-slate-700">{taggedCount}/{c.images?.length || 0}</span>
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          {/* Photo Selector Panel */}
          {currentCase && (
            <Card className="bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow p-5 rounded-xl">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-indigo-500" />
                Verification Photo Bundle
              </h3>

              <div className="grid grid-cols-1 gap-3">
                {currentCase.images.length === 0 ? (
                  <div className="py-8 text-center text-slate-400">
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 text-slate-300 animate-pulse" />
                    <p className="text-sm font-medium">No verification photos uploaded</p>
                  </div>
                ) : (
                  currentCase.images.map(img => {
                    const isSelected = img.id === selectedImageId;
                    return (
                      <div
                        key={img.id}
                        onClick={() => handleImageChange(img)}
                        className={`p-3 rounded-xl border cursor-pointer transition-all flex gap-3 ${isSelected
                          ? 'bg-indigo-50/50 border-indigo-200 shadow-sm'
                          : 'bg-white border-slate-100 hover:bg-slate-50 hover:border-slate-200'
                          }`}
                      >
                        {/* Image Preview Block */}
                        <div className="relative w-20 h-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200">
                          <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                          {img.isTagged && (
                            <div className="absolute top-1 right-1 bg-emerald-500 text-white rounded-full p-0.5 shadow-sm border border-white">
                              <Check className="w-2.5 h-2.5" />
                            </div>
                          )}
                        </div>

                        {/* Image Metadata Info */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                          <div>
                            <p className="text-sm font-bold text-slate-800 truncate">{img.label}</p>
                            {img.isTagged ? (
                              <div className="flex items-center gap-1 mt-1 text-[11px] text-emerald-600 font-bold font-mono bg-emerald-50/80 px-2 py-0.5 rounded w-max">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span>{img.lat.toFixed(4)}, {img.lng.toFixed(4)}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 mt-1 text-[11px] text-slate-500 font-semibold font-mono bg-slate-50 px-2 py-0.5 rounded w-max">
                                <Compass className="w-3 h-3 flex-shrink-0 text-slate-400" />
                                <span>NO COORDINATES</span>
                              </div>
                            )}
                          </div>

                          {/* Quick Photo Actions */}
                          <div className="flex items-center gap-2 mt-2 text-xs font-semibold text-slate-500">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDownloadImage(img); }}
                              className="hover:text-slate-800 transition-colors flex items-center gap-1"
                              title="Download Photograph with EXIF"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download
                            </button>
                            <span className="text-slate-200">|</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id); }}
                              className="text-rose-600 hover:text-rose-700 transition-colors flex items-center gap-1"
                              title="Remove Photograph"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          )}

        </div>

        {/* Right Side: Geocoding Simulator & Map (7 Cols) */}
        <div className="lg:col-span-7 space-y-6">
          {currentImage ? (
            <Card className="bg-white border border-slate-200/80 shadow-sm p-6 flex flex-col min-h-[640px] justify-between rounded-xl">

              {/* Photo Title Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  <span className="font-extrabold text-slate-800 text-base">{currentImage.label}</span>
                </div>
                <span className={`text-xs px-2.5 py-0.75 rounded-full font-bold uppercase tracking-wider ${currentImage.isTagged
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/50'
                  : 'bg-amber-50 text-amber-700 border border-amber-200/50 animate-pulse'
                  }`}>
                  {currentImage.isTagged ? 'EXIF Tagged' : 'Needs Geotag'}
                </span>
              </div>

              {/* Conditional Workflow States */}
              {!isTaggingActive ? (
                /* STEP 3 Call-To-Action (Untagged image screen) */
                <div className="flex-1 flex flex-col items-center justify-center py-16 px-6 text-center space-y-5 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl my-4">
                  <div className="bg-white p-4 rounded-full shadow-md border border-slate-100 flex items-center justify-center">
                    <Compass className="w-12 h-12 text-blue-500 animate-spin" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900">GPS EXIF Data Missing</h4>
                    <p className="text-slate-500 mt-2 text-sm max-w-sm mx-auto">
                      This lead verification picture is currently lacking latitude/longitude tags. Please initialize the geocoding session to place map markers and enter address files.
                    </p>
                  </div>
                  <Button
                    onClick={handleAddGeoTagClick}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-5 text-base rounded-lg shadow-md shadow-blue-100 hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Geo Tag
                  </Button>
                </div>
              ) : (
                /* STEP 4 & 5 Geocoding and Map Editor Panel */
                <div className="flex-1 flex flex-col justify-between my-4 space-y-5">

                  {/* Map Canvas */}
                  <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-slate-50 flex-1 min-h-[300px] shadow-inner">

                    {/* SVG Light Vector Map Backdrop */}
                    <div
                      className={`absolute inset-0 select-none cursor-crosshair overflow-hidden ${mapType === 'roadmap' ? 'bg-slate-100' : 'bg-slate-200/80'
                        }`}
                      onClick={handleMapClickOrDrag}
                      style={{
                        backgroundImage: 'radial-gradient(circle, rgba(99,102,241,0.1) 1.5px, transparent 1.5px)',
                        backgroundSize: '20px 20px'
                      }}
                    >
                      {/* Grid Simulated Streets */}
                      <div className="absolute inset-0 opacity-40">
                        <div className="absolute top-1/4 left-0 w-full h-[6px] bg-white shadow-sm" />
                        <div className="absolute top-2/3 left-0 w-full h-[6px] bg-white shadow-sm" />
                        <div className="absolute top-1/2 left-0 w-full h-[12px] bg-blue-200/50" />
                        <div className="absolute top-0 left-1/4 w-[6px] h-full bg-white shadow-sm" />
                        <div className="absolute top-0 left-2/3 w-[6px] h-full bg-white shadow-sm" />
                        <div className="absolute top-0 left-1/2 w-[12px] h-full bg-blue-200/50" />
                      </div>

                      {/* Parks / Green spaces */}
                      <div className="absolute top-[10%] left-[8%] w-[25%] h-[18%] rounded-xl bg-emerald-100/70 border border-emerald-200/60 flex items-center justify-center shadow-sm">
                        <span className="text-[10px] text-emerald-800 font-bold uppercase tracking-wider">Victoria Park</span>
                      </div>
                      <div className="absolute top-[62%] left-[55%] w-[38%] h-[28%] rounded-xl bg-sky-100/70 border border-sky-200/60 flex items-center justify-center shadow-sm">
                        <span className="text-[10px] text-blue-800 font-bold uppercase tracking-wider">Kankaria Lake Reserve</span>
                      </div>

                      {/* Landmarks */}
                      <div className="absolute top-[32%] left-[62%] bg-amber-50 border border-amber-200/50 px-2 py-0.5 rounded text-[9px] text-amber-800 font-bold shadow-sm">
                        ⭐ Metro Station A1
                      </div>

                      {/* Street Labels */}
                      <div className="absolute top-[46%] left-[10%] text-[9px] text-slate-500 font-bold font-mono tracking-wider rotate-[-1deg]">
                        UNIVERSITY ROAD
                      </div>
                      <div className="absolute top-[15%] left-[46%] text-[9px] text-slate-500 font-bold font-mono tracking-wider rotate-[90deg]">
                        AHMEDABAD BYPASS
                      </div>

                      {/* Draggable Marker Pin */}
                      <div
                        className="absolute -ml-3 -mt-6 cursor-grab active:cursor-grabbing transition-all duration-100 flex flex-col items-center"
                        style={{ left: `${pinPosition.x}px`, top: `${pinPosition.y}px` }}
                      >
                        <div className="bg-red-600 text-white p-1.5 rounded-full shadow-lg border border-red-500 animate-bounce">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div className="w-2.5 h-1 bg-slate-900/20 blur-[1.5px] rounded-full mt-0.5 shadow-sm" />
                      </div>
                    </div>

                    {/* Zoom HUD */}
                    <div className="absolute bottom-4 right-4 flex flex-col gap-1.5">
                      <button
                        onClick={() => setZoomLevel(prev => Math.min(18, prev + 1))}
                        className="w-8.5 h-8.5 rounded bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 flex items-center justify-center font-bold text-lg transition-colors shadow-sm"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setZoomLevel(prev => Math.max(10, prev - 1))}
                        className="w-8.5 h-8.5 rounded bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 flex items-center justify-center font-bold text-lg transition-colors shadow-sm"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Alert Banner / Instruction Badge */}
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm border border-slate-200/80 px-3.5 py-2 rounded-lg text-xs font-semibold text-slate-700 flex items-center gap-2 shadow-sm">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-ping" />
                      <span>Zoom: {zoomLevel}x</span>
                      <span className="text-slate-300">|</span>
                      <span>Drag Pin or Click Map to Update Coordinates</span>
                    </div>

                  </div>

                  {/* Manual Coordinates & Address Entry Form */}
                  <div className="space-y-4 pt-1">

                    {/* Part 1: Coordinates (Lat/Lng) */}
                    <div>
                      <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <Map className="w-3.5 h-3.5 text-blue-600" />
                        Coordinates Metadata
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Latitude */}
                        <div>
                          <label className="text-xs font-bold text-slate-600 block mb-1">Latitude Coordinate</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={latInput}
                              onChange={(e) => setLatInput(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-slate-800 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm transition-all"
                              placeholder="e.g. 23.0225"
                            />
                            <Compass className="w-4 h-4 text-slate-400 absolute right-3 top-2.5" />
                          </div>
                        </div>

                        {/* Longitude */}
                        <div>
                          <label className="text-xs font-bold text-slate-600 block mb-1">Longitude Coordinate</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={lngInput}
                              onChange={(e) => setLngInput(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-slate-800 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm transition-all"
                              placeholder="e.g. 72.5714"
                            />
                            <Compass className="w-4 h-4 text-slate-400 absolute right-3 top-2.5" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Part 2: Manual Address Override fields (Address, City, State, Pincode) */}
                    <div className="border-t border-slate-100 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5 text-indigo-500" />
                          Manual Address Override
                        </h4>

                        <Button
                          onClick={handleAddressFetch}
                          variant="ghost"
                          className="text-xs text-blue-600 hover:text-blue-700 bg-blue-50/50 hover:bg-blue-50 px-2.5 py-1 h-auto font-bold flex items-center gap-1 rounded"
                        >
                          <RefreshCw className="w-3 h-3 text-blue-600 animate-spin" />
                          Reverse Resolve GPS
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5">

                        {/* Complete Street Address */}
                        <div className="md:col-span-12">
                          <label className="text-xs font-bold text-slate-600 block mb-1">Street Address / House Description</label>
                          <input
                            type="text"
                            value={addressInput}
                            onChange={(e) => setAddressInput(e.target.value)}
                            className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-slate-800 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm transition-all"
                            placeholder="Apartment name, building number, road, landmark..."
                          />
                        </div>

                        {/* City */}
                        <div className="md:col-span-4">
                          <label className="text-xs font-bold text-slate-600 block mb-1">City</label>
                          <input
                            type="text"
                            value={cityInput}
                            onChange={(e) => setCityInput(e.target.value)}
                            className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-slate-800 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm transition-all"
                            placeholder="City"
                          />
                        </div>

                        {/* State */}
                        <div className="md:col-span-4">
                          <label className="text-xs font-bold text-slate-600 block mb-1">State</label>
                          <input
                            type="text"
                            value={stateInput}
                            onChange={(e) => setStateInput(e.target.value)}
                            className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-slate-800 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm transition-all"
                            placeholder="State"
                          />
                        </div>

                        {/* Pincode */}
                        <div className="md:col-span-4">
                          <label className="text-xs font-bold text-slate-600 block mb-1">Pincode</label>
                          <input
                            type="text"
                            value={pincodeInput}
                            onChange={(e) => setPincodeInput(e.target.value)}
                            className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-slate-800 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-sm transition-all"
                            placeholder="Pincode"
                          />
                        </div>

                      </div>
                    </div>

                    {/* Part 3: Form Actions (Reset & Save) */}
                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                      <Button
                        variant="ghost"
                        className="text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                        onClick={() => {
                          setIsTaggingActive(false);
                          toast.info('Form tracking state reset');
                        }}
                      >
                        Reset Tag
                      </Button>
                      <Button
                        onClick={handleSaveGeoTag}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 shadow-md shadow-blue-100 hover:shadow-lg transition-all"
                      >
                        Write Coordinates & Save
                      </Button>
                    </div>

                  </div>

                </div>
              )}

            </Card>
          ) : (
            /* empty state if no image is picked */
            <Card className="bg-white border border-slate-200/80 shadow-sm p-8 text-center py-24 flex flex-col items-center justify-center rounded-xl">
              <Compass className="w-16 h-16 text-slate-300 animate-spin mb-4" />
              <h3 className="text-lg font-bold text-slate-800">No Photo Selected</h3>
              <p className="text-slate-400 mt-2 max-w-sm text-sm font-medium">
                Please select an active verification case and select an image from the list bundle to start coordinates and manual address modifications.
              </p>
            </Card>
          )}
        </div>

      </div>
    </div>
  );
};

export default ManualGeoTagger;
